/**
 * Client access to the header nav document.
 *
 * Mirrors the prefetch discipline the mega-menu already uses: fetch once on idle, share
 * one module-level promise across every mounted header instance (desktop bar + mobile
 * drawer render separately), and never block the nav on it — the panels render their
 * labels from `config.ts` and fill in counts when this resolves.
 *
 * Only a COMPLETE document (`degraded: false`) is final. A cold server answers instantly
 * with an empty degraded document while it rebuilds in the background (~60s against the
 * test WP), so caching that first answer forever meant the panels stayed structure-only
 * for the whole session. Degraded or failed responses are displayed (best doc so far)
 * but re-polled with backoff until a complete one lands or the attempts run out.
 */

import { useEffect, useState } from 'react'
import { publicConfig } from 'config'
import { HEADER_NAV_ENDPOINT } from './config'
import type { HeaderNavDocument } from './types'

/**
 * `trailingSlash` applies to API routes too: hitting `/api/header-nav` without the slash
 * costs a 308 before every single fetch. Build the URL the way the app is configured.
 */
function endpoint(): string {
  return publicConfig('useTrailingSlash') ? `${HEADER_NAV_ENDPOINT}/` : HEADER_NAV_ENDPOINT
}

/**
 * Sized to outlast the cold rebuild: ~75s from the first response, against a rebuild
 * measured at 51–68s. Each poll is one small JSON read of the route's cache — cheap.
 */
const RETRY_DELAYS_MS = [5_000, 10_000, 20_000, 40_000] as const

/** Best document seen so far; final once `degraded` is false. */
let cached: HeaderNavDocument | null = null
let inFlight: Promise<HeaderNavDocument | null> | null = null

function isFinal(doc: HeaderNavDocument | null): doc is HeaderNavDocument {
  return doc !== null && !doc.degraded
}

/** Shared fetch — safe to call from anywhere; concurrent callers share one request. */
export function loadHeaderNav(): Promise<HeaderNavDocument | null> {
  if (isFinal(cached)) {
    return Promise.resolve(cached)
  }
  inFlight ??= fetch(endpoint(), { headers: { Accept: 'application/json' } })
    .then(res => (res.ok ? (res.json() as Promise<HeaderNavDocument>) : null))
    .then(doc => {
      // Advance monotonically: the placeholder EMPTY doc carries `generatedAt: 0`, so a
      // freshly-restarted server can never replace a populated document we already hold.
      if (doc && (cached === null || doc.generatedAt >= cached.generatedAt)) {
        cached = doc
      }
      return cached
    })
    .catch(() => cached)
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

/** Warm the cache before the reader opens anything (idle, or pointer-over the nav). */
export function prefetchHeaderNav(): void {
  void loadHeaderNav()
}

export function useHeaderNav(): { doc: HeaderNavDocument | null; loading: boolean } {
  const [doc, setDoc] = useState<HeaderNavDocument | null>(cached)
  const [loading, setLoading] = useState(!isFinal(cached))

  useEffect(() => {
    if (isFinal(cached)) {
      return undefined
    }
    let alive = true
    let idleId: number | undefined
    let timerId: ReturnType<typeof setTimeout> | undefined
    let attempt = 0

    const run = (): void => {
      void loadHeaderNav().then(res => {
        if (!alive) {
          return
        }
        setDoc(res)
        if (isFinal(res)) {
          setLoading(false)
          return
        }
        const delay = RETRY_DELAYS_MS[attempt]
        attempt += 1
        if (delay === undefined) {
          // Out of retries — settle for whatever arrived; the nav renders from config.
          setLoading(false)
          return
        }
        timerId = setTimeout(run, delay)
      })
    }

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 3000 })
    } else {
      timerId = setTimeout(run, 2000)
    }

    return () => {
      alive = false
      if (idleId !== undefined) {
        window.cancelIdleCallback(idleId)
      }
      if (timerId !== undefined) {
        clearTimeout(timerId)
      }
    }
  }, [])

  return { doc, loading }
}
